import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { NotificationQueueItem, NotificationType } from '../redux/types';

export interface CustomNotificationContextProps {
  notifications: NotificationQueueItem[];
  removeNotification: (id: number) => void;
}
export const CustomNotificationContext = createContext<CustomNotificationContextProps>({
  notifications: [],
  removeNotification: () => null,
});
CustomNotificationContext.displayName = 'CustomNotificationContext';

export interface CustomNotificationContextProviderProps {
  queueName: string;
  notifications: NotificationQueueItem[];
  removeNotification: (queueName: string, id: number) => void;
  children: React.ReactNode;
}

export const CustomNotificationProvider: React.FC<CustomNotificationContextProviderProps> = ({
  children,
  notifications,
  removeNotification,
  queueName,
}) => {
  const remove = useCallback(
    id => removeNotification(queueName, id),
    [queueName, removeNotification]
  );
  const props = useMemo(
    () => ({
      notifications,
      removeNotification: remove,
    }),
    [notifications, remove]
  );

  return (
    <CustomNotificationContext.Provider value={props}>
      {children}
    </CustomNotificationContext.Provider>
  );
};

export function useCustomNotifications(notificationTypes?: (string | NotificationType)[]) {
  const { notifications = [], removeNotification } = useContext(CustomNotificationContext);
  const filteredNotifications = notificationTypes
    ? notifications.filter(item => notificationTypes.includes(item.type))
    : notifications;
  return { notifications: filteredNotifications, removeNotification };
}
